import QueryString from "query-string";
import { getWWWDomainComponents } from "./domains";

import { AnyObject, Portal, Space } from "../models";
import getCustomDomainComponents from "./getCustomDomainComponents";
import { settings } from "shared";

type Context = {
  space?: Partial<Pick<Space, "id" | "subdomain" | "domain" | "access">>;
  portal?: Partial<Pick<Portal, "subdomain" | "domain" | "urlId" | "access">>;
  isPublic?: boolean;
};
/**
 * Builds an appropriate URL based on the path and context passed in
 *
 * @param {String} path The desired path, minus any consideration of space id or store
 * @param {Object} context An optional object to describe the space the url is for.
 *   If null, a base url is returned (i.e. a url outside the context of the store or a space)
 *
 * Example context
 * ```
 * {
 *   space: { id, subdomain }
 * }
 * ```
 */
export function buildURL(
  path: string = "/",
  { space, portal, isPublic }: Context = {},
  query = {},
  hash = ""
): string {
  const pathComponents: string[] = [];

  // Extract query parameters from the path, if present.
  const queryStringIndex = path.indexOf("?");
  let queryParams = {};
  if (queryStringIndex !== -1) {
    const pathWithoutQuery = path.substring(0, queryStringIndex);
    const queryString = path.substring(queryStringIndex + 1);

    queryParams = Object.fromEntries(new URLSearchParams(queryString));

    path = pathWithoutQuery;
  }

  query = { ...queryParams, ...query };

  if (portal?.subdomain || portal?.domain) {
    const portalOrigin = new URL(getOrigin(portal?.domain, portal?.subdomain));

    const isPrivateAccess = space?.access?.isPublic === false || portal.access?.isPublic === false;
    if (isPublic || !isPrivateAccess) {
      const fullPath = constructPath(path, 0, true, query, hash);
      return joinPath(portalOrigin.toString(), fullPath);
    }
  }

  if (portal) {
    pathComponents.push("p", portal.urlId);
  }

  const { id: spaceId, subdomain, domain } = space || {},
    origin = getOrigin(domain, subdomain);

  const fullPath = constructPath(
    joinPath(...pathComponents, path),
    spaceId,
    Boolean(subdomain || domain),
    query,
    hash
  );

  return `${origin}${fullPath}`;
}

/**
 * Builds an appropriate path based on the context passed in and hopeful that
 * the current subdomain matches the required one
 *
 * @param {String} path The desired path, minus any consideration of space id
 * @param {Object} context An optional object to describe the space context the url is for.
 *   If null, a base url is returned (i.e. a url outside the context of a space)
 *
 * Example context
 * ```
 * {
 *   space: { id, subdomain }
 * }
 * ```
 */
export function buildPath(
  path: string = "/",
  { space }: Context = {},
  query = {},
  hash = ""
): string {
  const { id: spaceId, subdomain } = space || {},
    { subdomain: currentSubdomain, identifier } = getCustomDomainComponents();
  if (
    settings.environment !== "production" &&
    currentSubdomain &&
    subdomain !== undefined && // Undefined subdomain will mean it was not convenient to supply it but can be assumed unimportant
    currentSubdomain !== subdomain
  ) {
    console.error("Cannot reach this space with only a path change!");
  }

  return constructPath(path, spaceId, Boolean(identifier), query, hash);
}

function joinPath(...components: string[]): string {
  return (
    components
      .reduce((acc, path) => {
        return `${acc.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
      }, "")
      // Remove the leading slash
      .replace(/^\//, "")
  );
}

/**
 * Attaches the appropriate prefix to the passed in path, based on the other context variables passed
 *
 * @param {String} path The desired path, minus any consideration of space id or store
 * @param {Number} spaceId The target space's id, if any
 * @param {Boolean} hasSubdomain Whether we are targeting a URL with a subdomain
 * @param {Object} query Search parameters in object notation
 * @param {String} hash URL hash
 *
 */
function constructPath(
  path: string,
  spaceId: number,
  hasSubdomain: boolean,
  query: AnyObject,
  hash: string
) {
  const spacePrefix = hasSubdomain || !spaceId ? "" : `/${spaceId}`,
    normalizedPath = `/${path || ""}`.replace("//", "/"),
    qs = QueryString.stringify(query),
    h = hash ? `#${hash}` : "";

  return [`${spacePrefix}${normalizedPath}`, qs].filter(Boolean).join("?").concat(h);
}

/**
 * Get the base url from the current window location and the subdomain passed in
 *
 * @param {String} [subdomain] A subdomain to use when building the host. Defaults to the standard lingo subdomain for the current environment.
 *
 */
function getOrigin(domain?: string, subdomain?: string): string {
  // Safe defaults for SSR
  let protocol = "https:";
  let port = "";

  // Only access window.location on client-side
  if (typeof window !== "undefined") {
    protocol = window.location.protocol;
    port = window.location.port;
  } else {
    const url = new URL(settings.wwwDomain);
    protocol = url.protocol;
    port = url.port;
  }

  const subdomainOrBase = subdomain || getBaseSubdomain();
  const host = domain || "lingoapp.com";
  const subdomainPlusPeriod = subdomainOrBase ? `${subdomainOrBase}.` : "";
  const portPlusColon = port ? `:${port}` : "";

  return `${protocol}//${subdomainPlusPeriod}${host}${portPlusColon}`;
}

function getBaseSubdomain() {
  return getWWWDomainComponents().subdomain;
}
